import { memo, useCallback, useEffect, useState } from 'react'
import styles from '../players/index.module.scss'
import { Button, Modal } from 'antd'
import { Document } from 'types/documents'
import { DocumentsTable } from '../../components/documents-table'
import { createDocument, deleteDocument, editDocument, getDocumentById, getDocuments } from 'services/documents'
import { DocumentForm } from '../../components/document-form'

export const Documents = memo(() => {
    const [documents, setDocuments] = useState<Document[]>([])
    const [document, setDocument] = useState<Document>({} as Document)
    const [open, setOpen] = useState<boolean>(false)
    const [loading, setLoading] = useState<boolean>(false)
    const [dataIsFetching, setDataIsFetching] = useState<boolean>(false)

    const getAllData = useCallback(() => {
        setDataIsFetching(true)

        const getAll = async function () {
            return await getDocuments()
        }

        getAll()
            .then(data => {
                setDocuments(data)
            })
            .finally(() => {
                setDataIsFetching(false)
            })
    }, [])

    useEffect(() => {
        getAllData()
    }, [getAllData])

    const toggleModal = useCallback(() => {
        setOpen(!open)
    }, [open])

    const handleAdd = useCallback(() => {
        setDocument({} as Document)
        toggleModal()
    }, [toggleModal])

    const handleEdit = useCallback(
        (id: number) => {
            setLoading(true)
            getDocumentById(id)
                .then(item => {
                    setDocument(item)
                    toggleModal()
                })
                .finally(() => {
                    setLoading(false)
                })
        },
        [toggleModal]
    )

    const handleDelete = useCallback(
        (id: number) => {
            setLoading(true)
            deleteDocument(id)
                .then(getAllData)
                .finally(() => {
                    setLoading(false)
                })
        },
        [getAllData]
    )

    const handleFormSubmit = useCallback(
        (submitValues: Document) => {
            setLoading(true)

            const refetchDataAndCloseModal = () => {
                getAllData()
                toggleModal()
            }

            if (document.id) {
                editDocument({
                    ...submitValues,
                    id: document.id,
                })
                    .then(refetchDataAndCloseModal)
                    .finally(() => {
                        setLoading(false)
                    })
            } else {
                createDocument(submitValues)
                    .then(refetchDataAndCloseModal)
                    .finally(() => {
                        setLoading(false)
                    })
            }
        },
        [getAllData, document, toggleModal]
    )

    const editMode = !!document.id
    const modalTitle = `${editMode ? `Edit document ${document.title}` : `Add new document`}`

    const somethingLoading = loading || dataIsFetching

    return (
        <div>
            Documents
            <div className={styles.actions}>
                <Button type={'primary'} onClick={handleAdd}>
                    Add
                </Button>
            </div>
            <DocumentsTable data={documents} onEdit={handleEdit} onDelete={handleDelete} loading={somethingLoading} />
            <Modal
                title={modalTitle}
                centered
                open={open}
                width={1000}
                destroyOnClose
                footer={null}
                onCancel={toggleModal}
                maskClosable={false}
            >
                <DocumentForm data={document} onSubmit={handleFormSubmit} loading={somethingLoading} />
            </Modal>
        </div>
    )
})
